import DateCell from '../overview/cells/DateCell'
import IconCell from '../overview/cells/IconCell'
import UserCell from '../overview/cells/UserCell'
import { UserData } from './DataSource'
import MainRoleCell from './MainRoleCell'
import CustomRolesCell from './CustomRolesCell'
import BadgesCell from './BadgesCell'
import RankCell from './RankCell'
import ProfileFieldCell from './ProfileFieldCell'
import GroupsCell from './GroupsCell'

export type CustomProfileFieldOptions = Record<string, string>

export interface CustomProfileField {
  id: string
  title: string
  type: string
  data?: CustomProfileFieldOptions
}

export type CustomProfileFieldList = CustomProfileField[]

interface Column {
  header: string
  accessor: string
  cell?: (props: UserData) => JSX.Element | null
  size: string
  sorting?: string
  isFixed?: boolean
  isHidden?: boolean
  tooltipMessage?: string
}

export interface Role {
  id: number
  name: string
  label: string
}

export interface RoleList {
  mainRoles: Role[]
  customRoles: Role[]
}

export interface Group {
  id: number
  title: string
}

export interface Badge {
  id: number
  title: string
}

export type BadgeList = Badge[]

export interface Rank {
  id: number
  value: string
}

export type RankList = Rank[]

const UsernameCell = () => {
  return (props: UserData) => {
    return UserCell('user', 24, false, true)({
      user: {
        id: props.user_id,
        username: props.username,
        avatar: props.avatar
      }
    })
  }
}

const usernameColumn: Column = {
  header: 'Username',
  accessor: 'username',
  cell: UsernameCell(),
  size: 'm-flexible',
  sorting: 'username',
  isFixed: true
}

const emailAddressColumn: Column = {
  header: 'Email',
  accessor: 'email',
  size: 'm-flexible',
  sorting: 'email'
}

const mainRoleColumn = (roles: Role[]): Column => ({
  header: 'Primary role',
  accessor: 'role',
  cell: (props: UserData) => {
    return <MainRoleCell roleId={props.role} roles={roles} />
  },
  size: 's'
})

const customRolesColumn = (roles: Role[]): Column => ({
  header: 'Custom roles',
  accessor: 'custom_roles',
  size: 'm-flexible',
  cell: (props: UserData) => {
    return <CustomRolesCell roleIds={props.custom_roles} roles={roles} />
  }
})

const groupsColumn = (groupList: Group[]): Column => ({
  header: 'Groups',
  accessor: 'groups',
  size: 'm-flexible',
  cell: (props: UserData) => {
    return <GroupsCell groupIds={props.groups} groupList={groupList} />
  },
  isHidden: true
})

const registeredAtColumn: Column = {
  header: 'Registered at',
  accessor: 'join_date',
  size: 's',
  cell: DateCell('join_date', true),
  sorting: 'join_date'
}

const lastLoginAtColumn: Column = {
  header: 'Last login',
  accessor: 'last_visit',
  size: 's',
  cell: (props: UserData) => {
    return props.last_visit === null ? 'Never logged in' : DateCell('last_visit', true)(props)
  },
  sorting: 'last_visit',
  isHidden: true
}

const badgesColumn = (badgeList: BadgeList): Column => ({
  header: 'Badges',
  accessor: 'badges',
  size: 's',
  cell: (props: UserData) => {
    return <BadgesCell badgeIds={props.badges} badgeList={badgeList} />
  },
  isHidden: true
})

const rankColumn = (rankList: RankList): Column => ({
  header: 'Rank',
  accessor: 'rank',
  size: 's',
  cell: (props: UserData) => {
    return <RankCell rankId={props.rank_id} rankList={rankList} />
  },
  isHidden: true
})

const lastActivityColumn: Column = {
  header: 'Last activity',
  accessor: 'last_activity',
  size: 's',
  cell: DateCell('last_activity', true),
  sorting: 'last_activity',
  tooltipMessage: 'Last activity updates every time a user performs any activity (eg. view a page, updated profile, liked a post)'
}

const repliesColumn: Column = {
  header: 'Replies',
  cell: IconCell('replies', 'reply'),
  size: 'xs-right',
  accessor: 'replies',
  sorting: 'replies'
}

const topicColumn: Column = {
  header: 'Topics',
  cell: IconCell('topics', 'article'),
  size: 'xs-right',
  accessor: 'topics',
  sorting: 'topics'
}

const profileFieldColumns = (customProfileFieldList: CustomProfileFieldList): Column[] => {
  return customProfileFieldList.map((customProfileField: CustomProfileField) => (
    {
      header: customProfileField.title,
      accessor: 'custom_profile_field_' + customProfileField.title.replace(/\s/g, '_'),
      cell: (props: UserData) => {
        return <ProfileFieldCell userProfileFields={props.profile_fields} profileFieldConfig={customProfileField} />
      },
      size: 'm-flexible',
      isHidden: true,
      label: 'User Profile Field'
    }
  ))
}

// const pointsColumn: Column = {
//   header: 'Points',
//   size: 'xs-right',
//   accessor: 'points',
//   sorting: 'points'
// }

export const getColumns = (
  badgeList: BadgeList,
  rankList: RankList,
  roleList: RoleList,
  groupList: Group[],
  customProfileFieldList: CustomProfileFieldList): Column[] => {
  const { mainRoles, customRoles } = roleList

  return [
    usernameColumn,
    emailAddressColumn,
    mainRoleColumn(mainRoles),
    customRolesColumn(customRoles),
    groupsColumn(groupList),
    registeredAtColumn,
    lastLoginAtColumn,
    lastActivityColumn,
    topicColumn,
    repliesColumn,
    // pointsColumn,
    badgesColumn(badgeList),
    rankColumn(rankList),
    ...profileFieldColumns(customProfileFieldList)
  ]
}
